import React, { useState } from 'react';
import { FREE_TRIAL_TOTAL, getFreeQuestionsUsed } from './FreeTrialBanner.jsx';
import { CLINICALS_PLANS_MONTHLY } from '../data/clinicalPlans.js';
import PromoBanner from './PromoBanner.jsx';
import { usePricingSectionView } from '../hooks/usePricingSectionView.js';

// Short marketing taglines shown under each tier's price on this landing page — presentational
// copy only (not price/credit facts), kept local since it's specific to this page's framing.
const TIER_TAGLINES = {
    class_prep_monthly: 'Block exams & early boards prep',
    board_review_monthly: 'Step 3 & COMLEX 3 — advanced reasoning engine',
    specialty_boards_monthly: 'ABIM, ABFM, ABEM, subspecialty & oral boards',
};

const SAMPLE_QUESTIONS = [
    {
        label: 'Emergency Medicine',
        tag: 'Management · Next Step · Advanced',
        source: 'Reversal of anticoagulation in life-threatening bleeding',
        stem: 'A 67-year-old male with a history of atrial fibrillation presents to the emergency department with severe abdominal pain and hematemesis. He was started on dabigatran 150 mg twice daily for stroke prevention two weeks ago. On examination, he is hypotensive (BP 88/54 mmHg) and tachycardic (HR 125 bpm). Hemoglobin is 7.2 g/dL. Urgent CT abdomen shows a large retroperitoneal hematoma. He is scheduled for emergent surgical intervention. INR is 1.1 and creatinine clearance is 45 mL/min. The surgical team requests immediate anticoagulation reversal.',
        question: 'Which of the following is the best immediate action for this patient?',
        choices: [
            { letter: 'A', text: 'Administer idarucizumab 5g IV' },
            { letter: 'B', text: 'Administer 4-factor prothrombin complex concentrate' },
            { letter: 'C', text: 'Administer fresh frozen plasma' },
            { letter: 'D', text: 'Switch to apixaban' },
            { letter: 'E', text: 'Withhold all anticoagulation and monitor' },
        ],
        correct: 'A',
        rationale: 'Idarucizumab is the specific reversal agent for dabigatran, indicated for life-threatening bleeding or emergent surgery. It binds dabigatran with high affinity, effectively reversing its anticoagulant effect within minutes. 4-factor PCC (B) is used for warfarin reversal. FFP (C) is not effective for DOAC reversal. Switching to apixaban (D) does not address immediate reversal. Withholding anticoagulation (E) is inadequate given the life-threatening hemorrhage requiring emergent surgery.',
        teaching: 'Idarucizumab = specific reversal for dabigatran. Andexanet alfa = specific reversal for Factor Xa inhibitors (apixaban, rivaroxaban). Know your DOAC reversal agents — they are high-yield on boards across all levels.',
        refs: '2023 ACC/AHA/ACCP/HRS Guideline for Anticoagulation Reversal · UpToDate: Management of bleeding in patients receiving DOACs',
    },
    {
        label: 'OB / Severe Preeclampsia',
        tag: 'Management · Next Step · Obstetrics',
        source: 'Preeclampsia with severe features — acute management',
        stem: 'A 32-year-old woman at 34 weeks of gestation presents to the emergency department with a severe headache and visual disturbances. Blood pressure is 168/112 mmHg.',
        labs: [
            { test: 'Platelets', value: '92,000/μL', ref: '150,000–450,000/μL', low: true },
            { test: 'Creatinine', value: '1.3 mg/dL', ref: '0.6–1.2 mg/dL', low: true },
            { test: 'Protein (urine dipstick)', value: '3+', ref: 'Negative', low: true },
        ],
        question: 'Which of the following is the best immediate action for this patient?',
        choices: [
            { letter: 'A', text: 'Administer intravenous magnesium sulfate' },
            { letter: 'B', text: 'Initiate oral labetalol therapy' },
            { letter: 'C', text: 'Perform immediate cesarean delivery' },
            { letter: 'D', text: 'Administer intravenous hydralazine' },
            { letter: 'E', text: 'Schedule induction of labor at 37 weeks' },
        ],
        correct: 'A',
        rationale: 'In preeclampsia with severe features, seizure prophylaxis with IV magnesium sulfate is the immediate priority. BP control is important but secondary — IV antihypertensives (labetalol, hydralazine, nifedipine) follow stabilization. Immediate cesarean (C) is not indicated without fetal distress or maternal instability. Scheduling induction at 37 weeks (E) is appropriate only for preeclampsia without severe features.',
        teaching: 'Severe preeclampsia priority sequence: (1) Magnesium sulfate for seizure prophylaxis → (2) IV antihypertensives for BP > 160/110 → (3) Delivery planning based on gestational age and maternal/fetal status.',
        refs: 'ACOG Practice Bulletin No. 222 (2020) · UpToDate: Management of preeclampsia',
    },
    {
        label: 'PA Students — Ortho',
        tag: 'Management · Next Step · PA Program',
        source: 'Acute knee injury — next best step in management',
        stem: 'A 25-year-old male presents to the emergency department after injuring his knee playing soccer. He reports hearing a \'pop\' at the time of injury and is now unable to bear weight on the affected leg. Examination reveals significant swelling and a positive Lachman test.',
        question: 'What is the next best step in management?',
        choices: [
            { letter: 'A', text: 'Order an MRI of the knee to confirm the diagnosis' },
            { letter: 'B', text: 'Perform knee arthroscopy to repair the ACL' },
            { letter: 'C', text: 'Initiate RICE protocol (rest, ice, compression, elevation)' },
            { letter: 'D', text: 'Refer immediately to an orthopedic surgeon' },
            { letter: 'E', text: 'Prescribe physical therapy for rehabilitation' },
        ],
        correct: 'C',
        rationale: 'Acute ACL tear management begins with conservative measures — RICE protocol reduces swelling, controls pain, and stabilizes the joint before further workup. MRI (A) confirms the diagnosis but is not the immediate next step. Arthroscopy (B) and PT (E) come after initial stabilization. Orthopedic referral (D) is appropriate but follows initial management in the acute ED setting.',
        teaching: 'Acute musculoskeletal injury sequence: RICE first → imaging to confirm → specialist referral → PT/surgical planning. The Lachman test is the most sensitive physical exam finding for ACL tear (sensitivity ~85%).',
        refs: '2022 AOSSM Guidelines for ACL Injury Management · UpToDate: Management of acute knee injuries',
    },
    {
        label: 'NP Students — Derm',
        tag: 'Mechanism / Pathophysiology · NP Program',
        source: 'Dermatology — psoriasis pathophysiology',
        stem: 'A 30-year-old woman presents with well-demarcated erythematous plaques covered with silvery scales on her elbows and knees. She reports the lesions have been present for several months and occasionally bleed when scratched (Auspitz sign).',
        question: 'What underlying mechanism is responsible for this patient\'s condition?',
        choices: [
            { letter: 'A', text: 'Increased keratinocyte proliferation and abnormal differentiation' },
            { letter: 'B', text: 'Autoimmune destruction of melanocytes in the skin' },
            { letter: 'C', text: 'Excessive sebum production leading to follicular occlusion' },
            { letter: 'D', text: 'Increased histamine release from mast cells' },
            { letter: 'E', text: 'Dysregulation of the hypothalamic-pituitary-adrenal axis' },
        ],
        correct: 'A',
        rationale: 'Psoriasis is driven by T-cell–mediated inflammation triggering excessive keratinocyte proliferation and abnormal differentiation — the epidermis turns over in 3–5 days instead of the normal 28–30 days, producing the characteristic thick plaques. Melanocyte destruction (B) = vitiligo. Follicular occlusion (C) = acne vulgaris. Histamine release (D) = urticaria/atopic dermatitis. HPA axis dysregulation (E) is unrelated.',
        teaching: 'Auspitz sign (pinpoint bleeding when scales removed) is pathognomonic for psoriasis. Mechanism: hyperproliferating keratinocytes driven by TNF-α, IL-17, IL-23. TNF-α and IL-17 are also the therapeutic targets (biologics).',
        refs: 'Kumar et al., Dermatology 5th ed. · UpToDate: Psoriasis — clinical features and diagnosis',
    },
    {
        label: 'Subspecialty Boards',
        tag: 'Gastroenterology · ASGE Quality Indicators · Advanced',
        source: 'ERCP prophylaxis — 2023 ASGE guidelines · Biliary obstruction',
        stem: 'A 52-year-old woman presents to the outpatient gastroenterology clinic with recurrent right upper quadrant pain and jaundice. She has a history of cholecystectomy 5 years ago. Current medications: atorvastatin 20 mg daily, omeprazole 20 mg daily. Exam shows mild RUQ tenderness without guarding. Labs: total bilirubin 3.2 mg/dL, alkaline phosphatase 320 U/L, normal amylase and lipase. Abdominal ultrasound shows a dilated common bile duct measuring 12 mm with no visible stones. ERCP is being considered to evaluate and manage suspected biliary obstruction.',
        question: 'According to current clinical guidelines, which of the following is the most appropriate approach?',
        choices: [
            { letter: 'A', text: 'Perform ERCP with rectal indomethacin prophylaxis' },
            { letter: 'B', text: 'Perform endoscopic ultrasound instead of ERCP' },
            { letter: 'C', text: 'Administer intravenous fluids and observe without intervention' },
            { letter: 'D', text: 'Schedule ERCP without prophylaxis' },
            { letter: 'E', text: 'Initiate a course of antibiotics before ERCP' },
        ],
        correct: 'A',
        rationale: 'The 2023 ASGE Quality Indicators recommend rectal indomethacin prophylaxis to reduce post-ERCP pancreatitis (PEP), especially in high-risk patients with a dilated CBD. EUS (B) provides no therapeutic intervention for confirmed obstruction. Observation (C) is inadequate with biochemical evidence of biliary obstruction. ERCP without prophylaxis (D) unnecessarily increases PEP risk. Antibiotics (E) are not a PEP prophylaxis strategy.',
        teaching: 'Rectal indomethacin 100 mg is standard PEP prophylaxis for all ERCP patients at elevated risk per 2023 ASGE guidelines — administer PR immediately before or after the procedure. EUS is used for diagnosis when ERCP risk outweighs benefit, not as a substitute when intervention is needed.',
        refs: '2023 ASGE Quality Indicators for Endoscopic Procedures · UpToDate: Prevention of post-ERCP pancreatitis',
    },
];

const PATHWAY_LIST = [
    {
        slug: 'medical-students',
        title: 'Medical Student',
        pitch: 'Block exams, shelf exams, and USMLE/COMLEX boards — from your own notes.',
        tags: ['USMLE Step 1 & 2', 'COMLEX L1 & L2', 'Shelf Exams'],
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
        ),
    },
    {
        slug: 'residents',
        title: 'Resident / Fellow',
        pitch: 'Rounds prep, Step 3, and specialty board questions from your patient notes.',
        tags: ['USMLE Step 3', 'COMLEX L3', 'Specialty Boards'],
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
        ),
    },
    {
        slug: 'np',
        title: 'Nurse Practitioner',
        pitch: 'Didactic class prep through FNP and ACNP certification exams.',
        tags: ['FNP (AANP/ANCC)', 'ACNP', 'Class Prep'],
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
        ),
    },
    {
        slug: 'crna',
        title: 'CRNA',
        pitch: 'Anesthesia-specific questions no generic Q-bank can match.',
        tags: ['NBCRNA NCE', 'Class Prep'],
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
        ),
    },
    {
        slug: 'pa',
        title: 'Physician Assistant',
        pitch: 'Didactic phase through PANCE and PANRE — from your own study materials.',
        tags: ['PANCE', 'PANRE', 'Class Prep'],
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
        ),
    },
    {
        slug: 'aa',
        title: 'Anesthesia Assistant',
        pitch: 'CAA-specific prep from your didactic materials through NCCAA certification.',
        tags: ['NCCAA', 'Class Prep'],
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
    },
    {
        slug: 'nursing',
        title: 'Nursing Student',
        pitch: 'Block exams and NCLEX-style priority action questions from your lecture notes.',
        tags: ['NCLEX-RN', 'NCLEX-PN', 'Block Exams'],
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m0 5h-3m-3 0h.01M9 16h.01" />
            </svg>
        ),
    },
    {
        slug: 'allied-health',
        title: 'Allied Health',
        pitch: 'Class prep and specialty certification from any allied health program.',
        tags: ['Class Prep', 'Certification Exams'],
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
        ),
    },
    {
        slug: 'clinicians',
        title: 'Practicing Clinician',
        pitch: 'Exhausted your Q-banks? Generate unlimited specialty questions from your own materials.',
        tags: ['ABIM / ABFM / ABS', 'MOC', 'Specialty Boards'],
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
        ),
    },
];

const STEPS = [
    {
        n: '01',
        title: 'Record audio, upload or snap a picture of your materials',
        desc: 'Drop in lecture slides, class notes, a whiteboard photo, or record live audio. Any format.',
    },
    {
        n: '02',
        title: 'Questions tuned to your exam',
        desc: 'MCQs styled precisely for your program — block exam, shelf, Step, NCLEX, or boards. Not generic banks.',
    },
    {
        n: '03',
        title: 'Build your personal Q-bank',
        desc: 'Every question you generate is saved. Review by subject, track accuracy, study the ones you miss.',
    },
];

const FEATURES = [
    {
        title: 'Exam-tuned question format',
        desc: 'MS1 block exams get mechanism questions. Step 2 and NCLEX get vignettes. Boards get 400-word cases. Format matches your actual exam.',
        d: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
    },
    {
        title: 'From your own source material',
        desc: 'Paste professor notes, upload a whiteboard photo, or record live audio. The engine extracts the concept and writes the question.',
        d: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
    },
    {
        title: 'Timed practice exams, not just flashcards',
        desc: "Turn any folder into a real multiple-choice exam — timed, scored, board-simulation format. This is what separates studying from actually testing yourself.",
        d: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    },
    {
        title: 'Quality checked, community verified',
        desc: 'Every question is held to a high accuracy standard. See something off anyway? Flag it in one tap — reports get reviewed and corrected, keeping the question bank sharp for everyone.',
        d: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    },
    {
        title: 'Personal Q-bank that grows',
        desc: 'Every question you generate is banked by subject. Review performance by topic, retry wrong answers, watch accuracy improve.',
        d: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    },
    {
        title: 'Detailed rationales & teaching points',
        desc: 'Every question includes a full explanation of why each answer is right or wrong — plus the teaching point to carry into the exam.',
        d: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
    },
];

const TESTIMONIALS = [
    {
        quote: "Generated an entire shelf exam Q-bank from my internal medicine rotation notes in one afternoon. The vignette style matched exactly what I saw on the actual shelf.",
        name: 'Alex Chen',
        role: 'MS3 · Internal Medicine Shelf',
    },
    {
        quote: "The recording feature is the best part — I set the interval and it creates a deck automatically from my live and pre-recorded lectures. It's the material that actually showed up on my exam, not random stuff from someone else's Anki deck. The question engine is truly high-end.",
        name: 'Jordan Williams',
        role: 'SRNA · NBCRNA NCE Prep, Class of 2026',
    },
    {
        quote: "Finding FNP-specific practice questions in my clinical area was nearly impossible. FlashFonic let me build exactly the Q-bank I needed directly from my rotation notes.",
        name: 'Sarah Martinez',
        role: 'FNP Student · AANP Certification Prep',
    },
    {
        quote: "Took photos of my Step 3 review notes from rounds and had 40 high-yield questions in under 10 minutes. The clinical management framing was spot-on.",
        name: 'Dr. Michael Torres',
        role: 'PGY-3 Internal Medicine · USMLE Step 3',
    },
];

const LandingPage = ({ onEnter, onOpenPlanChooser, onOpenAccount }) => {
    const pricingSectionRef = usePricingSectionView('/');
    const questionsUsed = getFreeQuestionsUsed();
    const freeRemaining = Math.max(0, FREE_TRIAL_TOTAL - questionsUsed);
    const hasFreeLeft = freeRemaining > 0;
    const [demoTab, setDemoTab] = useState(0);
    const [demoSelected, setDemoSelected] = useState(null);
    const [demoRevealed, setDemoRevealed] = useState(false);

    const handleStart = () => {
        if (typeof onEnter === 'function') onEnter();
    };

    const handleDemoTab = (idx) => {
        setDemoTab(idx);
        setDemoSelected(null);
        setDemoRevealed(false);
    };

    const handleDemoChoice = (letter) => {
        if (demoRevealed) return;
        setDemoSelected(letter);
        setDemoRevealed(true);
    };

    const demoQ = SAMPLE_QUESTIONS[demoTab];

    return (
        <div className="min-h-screen bg-[#080e1a] text-slate-200" style={{ fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif' }}>

            {/* Background glows */}
            <div aria-hidden="true" className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-96 w-[52rem] rounded-full bg-teal-500/8 blur-3xl" />
                <div className="absolute top-1/3 -right-24 h-80 w-80 rounded-full bg-emerald-500/6 blur-3xl" />
                <div className="absolute bottom-0 -left-16 h-80 w-80 rounded-full bg-teal-600/5 blur-3xl" />
            </div>

            {/* Nav */}
            <nav className="relative z-10 border-b border-white/5 bg-black/20 backdrop-blur-sm">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 flex items-center justify-between gap-2 py-3">
                    <a href="/" className="flex items-center gap-2 shrink-0 min-w-0">
                        <div className="leading-none">
                            <p className="text-2xl sm:text-[28px] font-semibold tracking-tight">
                                <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">FlashFonic</span>
                                <span className="align-top text-teal-400/70 text-xs">™</span>
                            </p>
                            <p className="text-[11px] sm:text-xs text-slate-400 mt-0.5">Listen. Flash it. Learn.</p>
                        </div>
                    </a>
                    <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                        <a href="#pricing" className="hidden sm:block text-sm text-slate-400 hover:text-white transition">Pricing</a>
                        <button onClick={onOpenAccount} className="text-sm text-slate-400 hover:text-white transition">
                            Sign in
                        </button>
                        <button
                            onClick={handleStart}
                            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:opacity-90 px-3 sm:px-4 py-1.5 text-sm font-medium text-white transition shadow-lg shadow-teal-500/20"
                        >
                            Get Started
                        </button>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="relative z-10 pt-20 pb-14 px-4 sm:px-6 text-center">
                <div className="mx-auto max-w-3xl">
                    <div className="inline-flex items-center gap-2 rounded-full border border-teal-400/25 bg-teal-500/10 px-4 py-1.5 text-xs font-medium text-teal-400 mb-7">
                        <span className="h-1.5 w-1.5 rounded-full bg-teal-400 animate-pulse" />
                        Built by a physician · For every stage of clinical training
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
                        The study tool built for<br />
                        <span className="bg-gradient-to-r from-teal-400 via-emerald-400 to-cyan-300 bg-clip-text text-transparent">
                            how clinicians actually learn.
                        </span>
                    </h1>
                    <div className="mt-6 max-w-2xl mx-auto space-y-2">
                        <p className="text-lg text-slate-300 leading-relaxed font-medium">
                            Hit record in lecture. Snap your whiteboard. Upload your notes.
                            <br />
                            <span className="text-teal-400">High quality study decks and board quality questions appear in seconds.</span>
                        </p>
                        <p className="text-base text-slate-500 leading-relaxed">
                            Scribe full concept notes. Organize every topic. Find exactly where you&apos;re weak — then drill it until you&apos;re not. Built from the material you&apos;ll actually be tested on, not generic decks or question banks someone else wrote.
                        </p>
                    </div>
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <button
                            onClick={handleStart}
                            className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 px-8 py-4 text-base font-semibold text-white shadow-xl shadow-teal-500/25 transition"
                        >
                            {hasFreeLeft ? `Start free — ${freeRemaining} questions included` : 'Get started'}
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                        </button>
                        <a href="#pathways" className="text-sm text-slate-400 hover:text-teal-400 transition">
                            See who it&apos;s for ↓
                        </a>
                    </div>
                    {hasFreeLeft && (
                        <p className="mt-4 text-xs text-slate-600">No credit card required · {freeRemaining} of {FREE_TRIAL_TOTAL} free questions remaining</p>
                    )}
                </div>
            </section>

            {/* Pain point / differentiation */}
            <section className="relative z-10 py-20 px-4 sm:px-6 border-b border-white/5">
                <div className="mx-auto max-w-4xl text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">Tired of generic question banks?</h2>
                    <p className="mt-5 text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
                        We&apos;ve all been there. There&apos;s one topic you just wish you could drill with two-, three-, even four-step reasoning questions before your exam.
                        <br className="hidden sm:block" />
                        <span className="text-teal-400 font-medium">Now it&apos;s at your fingertips.</span>
                    </p>
                </div>
                <div className="mx-auto max-w-4xl mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
                        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Generic question banks</p>
                        <ul className="space-y-3 text-sm text-slate-400">
                            <li className="flex items-start gap-2"><span className="text-slate-600 mt-0.5">✗</span> Unfocused — written for everyone, tailored to no one</li>
                            <li className="flex items-start gap-2"><span className="text-slate-600 mt-0.5">✗</span> The same few hundred questions, over and over</li>
                            <li className="flex items-start gap-2"><span className="text-slate-600 mt-0.5">✗</span> No way to drill just the one topic you&apos;re shaky on</li>
                        </ul>
                    </div>
                    <div className="rounded-2xl border border-teal-500/30 bg-teal-500/5 p-6">
                        <p className="text-xs font-semibold uppercase tracking-widest text-teal-400 mb-4">FlashFonic</p>
                        <ul className="space-y-3 text-sm text-slate-200">
                            <li className="flex items-start gap-2"><span className="text-teal-400 mt-0.5 font-bold">✓</span> Built from your own lecture, whiteboard, and notes</li>
                            <li className="flex items-start gap-2"><span className="text-teal-400 mt-0.5 font-bold">✓</span> Multi-step clinical reasoning, as deep as you need it</li>
                            <li className="flex items-start gap-2"><span className="text-teal-400 mt-0.5 font-bold">✓</span> Endless fresh questions on the exact topic you flag</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="relative z-10 py-20 px-4 sm:px-6 border-b border-white/5">
                <div className="mx-auto max-w-4xl">
                    <h2 className="text-center text-3xl sm:text-4xl font-bold tracking-tight text-white mb-16">How it works</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-6">
                        {STEPS.map((s, i) => (
                            <div key={i} className="flex flex-col items-start gap-4">
                                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500/20 to-emerald-500/20 border border-teal-400/20 text-sm font-bold text-teal-400 tabular-nums">
                                    {s.n}
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold text-white mb-2">{s.title}</h3>
                                    <p className="text-xs text-slate-400 leading-relaxed">{s.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* From a photo to a real exam question */}
            <section className="relative z-10 py-20 px-4 sm:px-6 border-b border-white/5 bg-[rgba(255,255,255,0.012)]">
                <div className="mx-auto max-w-6xl">
                    <div className="text-center mb-14">
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">From your notes to exam-ready</h2>
                        <p className="mt-3 text-slate-400 text-sm max-w-xl mx-auto">One real example, start to finish — the exact screens you&apos;ll use in the app.</p>
                    </div>

                    <div className="flex flex-col lg:flex-row items-stretch gap-5">

                        {/* 1. Record, snap it, or upload */}
                        <div className="flex-1 flex flex-col gap-3">
                            <p className="text-center text-[11px] font-semibold uppercase tracking-widest text-slate-500">1. Record, snap it, or upload</p>
                            <div className="flex-1 flex items-center gap-3">
                                <div className="flex-1 rounded-xl bg-white p-3 shadow-lg shadow-black/30 -rotate-2">
                                    <p className="text-[8px] font-bold text-slate-400 mb-1.5 tracking-wide uppercase">My notes</p>
                                    <div className="space-y-1">
                                        <div className="h-1.5 w-full bg-slate-300 rounded-full" />
                                        <div className="h-1.5 w-4/5 bg-slate-300 rounded-full" />
                                        <div className="h-1.5 w-3/5 bg-teal-500/60 rounded-full" />
                                        <div className="h-1.5 w-full bg-slate-300 rounded-full" />
                                        <div className="h-1.5 w-2/3 bg-slate-300 rounded-full" />
                                    </div>
                                    <p className="mt-2 text-[7px] text-slate-500 italic leading-tight">psoriasis — silvery scale, Auspitz sign...</p>
                                </div>
                                <div className="flex-1 rounded-xl bg-white p-3 shadow-lg shadow-black/30 rotate-2">
                                    <p className="text-[8px] font-bold text-slate-400 mb-1.5 tracking-wide uppercase">Textbook</p>
                                    <div className="space-y-1">
                                        <div className="h-1.5 w-full bg-slate-300 rounded-full" />
                                        <div className="h-1.5 w-full bg-slate-300 rounded-full" />
                                        <div className="h-1.5 w-4/5 bg-slate-300 rounded-full" />
                                        <div className="h-1.5 w-full bg-slate-300 rounded-full" />
                                        <div className="h-1.5 w-1/2 bg-slate-300 rounded-full" />
                                    </div>
                                    <p className="mt-2 text-[7px] text-slate-500 italic leading-tight">Ch. 12 — Papulosquamous disorders</p>
                                </div>
                            </div>
                            <div className="flex justify-center">
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-400/25 bg-teal-500/10 px-2.5 py-1 text-[10px] font-medium text-teal-400">
                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                        <circle cx="12" cy="13" r="3.5" />
                                    </svg>
                                    Record, snap, or upload
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center justify-center shrink-0">
                            <svg className="h-5 w-5 text-teal-500/40 rotate-90 lg:rotate-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>

                        {/* 2. Review queue */}
                        <div className="flex-1 flex flex-col gap-3">
                            <p className="text-center text-[11px] font-semibold uppercase tracking-widest text-slate-500">2. Lands in your queue</p>
                            <div className="flex-1 rounded-2xl bg-slate-900/60 border border-white/10 p-3 shadow-lg shadow-black/20">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-xs font-semibold tracking-tight text-white">Review Queue</h4>
                                    <span className="text-[9px] rounded-lg px-1.5 py-0.5 text-slate-300 bg-slate-800/70 ring-1 ring-white/10">1 new</span>
                                </div>
                                <div className="rounded-xl bg-slate-900/70 ring-1 ring-white/10 p-2.5">
                                    <p className="text-[11px] text-slate-200 leading-snug"><strong>Q:</strong> What underlying mechanism is responsible for this patient&apos;s condition?</p>
                                    <p className="text-[11px] text-slate-400 mt-1.5 leading-snug"><strong>A:</strong> Increased keratinocyte proliferation and abnormal differentiation</p>
                                    <div className="mt-2 flex gap-1.5">
                                        <span className="text-[9px] rounded-md px-1.5 py-0.5 bg-slate-800 text-slate-400">Edit</span>
                                        <span className="text-[9px] rounded-md px-1.5 py-0.5 bg-slate-800 text-slate-400">Delete</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-center shrink-0">
                            <svg className="h-5 w-5 text-teal-500/40 rotate-90 lg:rotate-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>

                        {/* 3. Drill it */}
                        <div className="flex-1 flex flex-col gap-3">
                            <p className="text-center text-[11px] font-semibold uppercase tracking-widest text-slate-500">3. Drill it</p>
                            <div className="flex-1 flex gap-2">
                                <div className="flex-1 rounded-xl border border-fuchsia-500/25 bg-gradient-to-br from-slate-950 to-slate-900 ring-1 ring-fuchsia-500/15 shadow-lg shadow-fuchsia-500/10 p-3 flex flex-col">
                                    <span className="text-[8px] font-semibold uppercase tracking-widest text-fuchsia-400/70">Question</span>
                                    <p className="mt-1.5 text-[10px] text-slate-100 leading-snug flex-1">What mechanism drives silvery, well-demarcated psoriatic plaques?</p>
                                </div>
                                <div className="flex-1 rounded-xl border border-violet-500/25 bg-gradient-to-br from-slate-950 to-slate-900/90 ring-1 ring-violet-500/15 shadow-lg shadow-violet-500/10 p-3 flex flex-col">
                                    <span className="text-[8px] font-semibold uppercase tracking-widest text-violet-400/70">Answer</span>
                                    <p className="mt-1.5 text-[10px] text-slate-100 leading-snug flex-1">Keratinocyte proliferation + abnormal differentiation</p>
                                </div>
                            </div>
                            <div className="flex justify-center gap-1.5">
                                <span className="rounded-full px-2 py-0.5 text-[9px] font-medium border border-rose-500/40 text-rose-300">Again</span>
                                <span className="rounded-full px-2 py-0.5 text-[9px] font-medium border border-amber-500/40 text-amber-300">Hard</span>
                                <span className="rounded-full px-2 py-0.5 text-[9px] font-medium border border-emerald-500/40 text-emerald-300">Good</span>
                                <span className="rounded-full px-2 py-0.5 text-[9px] font-medium border border-sky-500/40 text-sky-300">Easy</span>
                            </div>
                        </div>

                        <div className="flex items-center justify-center shrink-0">
                            <svg className="h-5 w-5 text-teal-500/40 rotate-90 lg:rotate-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>

                        {/* 4. On your exam */}
                        <div className="flex-1 flex flex-col gap-3">
                            <p className="text-center text-[11px] font-semibold uppercase tracking-widest text-slate-500">4. Shows up on your exam</p>
                            <div className="flex-1 rounded-2xl border border-white/10 bg-[#0d1626] p-3 shadow-lg shadow-black/30">
                                <span className="inline-flex text-[9px] font-semibold text-teal-400 border border-teal-400/25 bg-teal-500/10 rounded-full px-2 py-0.5 mb-2">Mechanism · Dermatology</span>
                                <p className="text-[10px] text-slate-300 leading-snug">A 30-year-old woman presents with well-demarcated erythematous plaques with silvery scales on her elbows and knees...</p>
                                <div className="mt-2 space-y-1">
                                    <div className="rounded-lg border border-emerald-400/50 bg-emerald-500/10 px-2 py-1 text-[9px] text-emerald-300">A. Increased keratinocyte proliferation and abnormal differentiation</div>
                                    <div className="rounded-lg border border-white/6 px-2 py-1 text-[9px] text-slate-500">B. Autoimmune destruction of melanocytes</div>
                                    <div className="rounded-lg border border-white/6 px-2 py-1 text-[9px] text-slate-500">C. Excessive sebum, follicular occlusion</div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* Live demo — sample questions */}
            <section className="relative z-10 py-20 px-4 sm:px-6 border-b border-white/5">
                <div className="mx-auto max-w-4xl">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-500/8 px-3 py-1 text-xs font-medium text-emerald-400 mb-4">
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Real output · Generated from actual study notes
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">See what it generates</h2>
                        <p className="mt-2 text-sm text-slate-500">Clinical vignettes styled exactly for your exam. Click an answer to see the rationale.</p>
                    </div>

                    {/* Tab switcher */}
                    <div className="flex gap-2 justify-center flex-wrap mb-8">
                        {SAMPLE_QUESTIONS.map((q, i) => (
                            <button
                                key={i}
                                onClick={() => handleDemoTab(i)}
                                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition border ${
                                    demoTab === i
                                        ? 'bg-teal-500/20 border-teal-400/50 text-teal-300'
                                        : 'border-white/10 text-slate-500 hover:text-slate-300 hover:border-white/20 bg-transparent'
                                }`}
                            >
                                {q.label}
                            </button>
                        ))}
                    </div>

                    {/* Question card */}
                    <div className="rounded-2xl border border-white/10 bg-[#0d1626] overflow-hidden shadow-2xl">
                        {/* Card header */}
                        <div className="border-b border-white/6 px-6 py-3 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-400/25 bg-teal-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-teal-400">
                                    {demoQ.tag}
                                </span>
                            </div>
                            <span className="text-[11px] text-slate-600 italic truncate max-w-xs">{demoQ.source}</span>
                        </div>

                        {/* Stem */}
                        <div className="px-6 pt-6 pb-4 space-y-4">
                            <p className="text-sm text-slate-200 leading-relaxed">{demoQ.stem}</p>
                            {demoQ.labs && (
                                <div className="overflow-x-auto rounded-xl border border-white/8 bg-white/[0.025]">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="border-b border-white/8">
                                                <th className="px-4 py-2 text-left font-semibold text-slate-400">Test</th>
                                                <th className="px-4 py-2 text-left font-semibold text-slate-400">Value</th>
                                                <th className="px-4 py-2 text-left font-semibold text-slate-400">Reference</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {demoQ.labs.map((row, ri) => (
                                                <tr key={ri} className="border-b border-white/5 last:border-0">
                                                    <td className="px-4 py-2 text-slate-300">{row.test}</td>
                                                    <td className={`px-4 py-2 font-semibold ${row.low ? 'text-red-400' : 'text-emerald-400'}`}>{row.value}</td>
                                                    <td className="px-4 py-2 text-slate-500">{row.ref}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {demoQ.question && (
                                <p className="text-sm font-semibold text-white border-l-2 border-teal-400/60 pl-3">{demoQ.question}</p>
                            )}
                        </div>

                        {/* Choices */}
                        <div className="px-6 pb-5 grid grid-cols-1 gap-2">
                            {demoQ.choices.map(c => {
                                const isCorrect = c.letter === demoQ.correct;
                                const isSelected = c.letter === demoSelected;
                                let style = 'border-white/10 bg-white/[0.03] text-slate-300 hover:border-teal-400/40 hover:bg-teal-500/5 cursor-pointer';
                                if (demoRevealed && isCorrect) style = 'border-emerald-400/60 bg-emerald-500/10 text-emerald-300 cursor-default';
                                else if (demoRevealed && isSelected && !isCorrect) style = 'border-red-400/50 bg-red-500/8 text-red-300 cursor-default';
                                else if (demoRevealed) style = 'border-white/6 bg-transparent text-slate-500 cursor-default';
                                return (
                                    <button
                                        key={c.letter}
                                        onClick={() => handleDemoChoice(c.letter)}
                                        className={`w-full text-left flex items-start gap-3 rounded-xl border px-4 py-3 text-sm transition ${style}`}
                                    >
                                        <span className={`shrink-0 h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold mt-0.5 ${
                                            demoRevealed && isCorrect ? 'bg-emerald-500/30 text-emerald-300' :
                                            demoRevealed && isSelected ? 'bg-red-500/25 text-red-300' :
                                            'bg-white/6 text-slate-400'
                                        }`}>{c.letter}</span>
                                        <span className="leading-snug">{c.text}</span>
                                        {demoRevealed && isCorrect && (
                                            <svg className="ml-auto h-5 w-5 text-emerald-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                        {demoRevealed && isSelected && !isCorrect && (
                                            <svg className="ml-auto h-5 w-5 text-red-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Rationale */}
                        {demoRevealed && (
                            <div className="border-t border-white/6 px-6 py-5 space-y-3">
                                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Rationale</p>
                                <p className="text-sm text-slate-300 leading-relaxed">{demoQ.rationale}</p>
                                <div className="rounded-xl border border-teal-500/20 bg-teal-500/5 px-4 py-3">
                                    <p className="text-xs font-semibold text-teal-400 mb-1">Teaching Point</p>
                                    <p className="text-xs text-teal-300/80 leading-relaxed">{demoQ.teaching}</p>
                                </div>
                                {demoQ.refs && (
                                    <p className="text-[11px] text-slate-600 leading-relaxed">
                                        <span className="font-semibold text-slate-500">References: </span>{demoQ.refs}
                                    </p>
                                )}
                                <div className="pt-2 flex items-center justify-between">
                                    {demoTab < SAMPLE_QUESTIONS.length - 1 ? (
                                        <button onClick={() => handleDemoTab(demoTab + 1)} className="text-xs text-teal-400 hover:text-teal-300 transition">
                                            Next example →
                                        </button>
                                    ) : (
                                        <button onClick={() => handleDemoTab(0)} className="text-xs text-teal-400 hover:text-teal-300 transition">
                                            Start from beginning ↺
                                        </button>
                                    )}
                                    <button onClick={handleStart} className="rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:opacity-90 px-4 py-2 text-xs font-semibold text-white transition">
                                        Generate from my notes →
                                    </button>
                                </div>
                            </div>
                        )}

                        {!demoRevealed && (
                            <div className="border-t border-white/5 px-6 py-3 text-center">
                                <p className="text-xs text-slate-600">Select an answer to see the rationale</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Founder credibility band */}
            <section className="relative z-10 py-12 px-4 sm:px-6 border-y border-white/5 bg-[rgba(255,255,255,0.015)]">
                <div className="mx-auto max-w-4xl">
                    <div className="flex flex-col sm:flex-row items-start gap-8">
                        <div className="shrink-0 flex flex-col items-center sm:items-start gap-3">
                            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-teal-500/20 to-emerald-500/20 border border-teal-400/20 flex items-center justify-center">
                                <svg className="h-7 w-7 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                </svg>
                            </div>
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-400 uppercase tracking-wide">
                                Physician Built
                            </span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-base sm:text-lg text-slate-300 leading-relaxed italic">
                                &ldquo;Studying for my certification boards in Rheumatology back in 2025, I was amazed at the high cost and limited prep questions from the available sources. I developed FlashFonic so I could use my own study materials to generate exactly the questions I needed. Passed with flying colors. Now that tool serves every stage of clinical training.&rdquo;
                            </p>
                            <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                                <div className="hidden sm:block h-px flex-1 bg-white/8" />
                                <p className="text-sm font-semibold text-teal-400 sm:shrink-0">Dr. M. Stan, DO · Founder, FlashFonic</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pathway cards */}
            <section id="pathways" className="relative z-10 py-20 px-4 sm:px-6 border-b border-white/5">
                <div className="mx-auto max-w-6xl">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">Built for your stage of training</h2>
                        <p className="mt-3 text-slate-400 text-sm max-w-xl mx-auto">Choose your path — every role, every exam, questions styled for where you are right now.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {PATHWAY_LIST.map(p => (
                            <a
                                key={p.slug}
                                href={`/for/${p.slug}`}
                                className="group rounded-2xl border border-white/8 bg-white/[0.03] hover:border-teal-500/40 hover:bg-teal-500/5 hover:-translate-y-0.5 transition-all duration-200 flex flex-col gap-3 p-5"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="h-9 w-9 shrink-0 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400 group-hover:bg-teal-500/20 transition">
                                        {p.icon}
                                    </div>
                                    <h3 className="text-sm font-semibold text-white">{p.title}</h3>
                                </div>
                                <p className="text-xs text-slate-400 leading-relaxed">{p.pitch}</p>
                                <div className="flex flex-wrap gap-1.5 mt-auto">
                                    {p.tags.map(tag => (
                                        <span key={tag} className="inline-block rounded-full border border-teal-500/25 bg-teal-500/8 px-2.5 py-0.5 text-[11px] font-medium text-teal-300">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                                <div className="flex items-center gap-1 text-xs text-teal-400 group-hover:text-teal-300 transition mt-1">
                                    Learn more
                                    <svg className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="relative z-10 py-20 px-4 sm:px-6 border-b border-white/5">
                <div className="mx-auto max-w-5xl">
                    <h2 className="text-center text-3xl sm:text-4xl font-bold tracking-tight text-white mb-12">Everything built in</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {FEATURES.map((f, i) => (
                            <div key={i} className="rounded-2xl border border-white/6 bg-white/[0.018] hover:border-teal-500/20 transition p-5">
                                <div className="h-9 w-9 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center mb-4">
                                    <svg className="h-5 w-5 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={f.d} />
                                    </svg>
                                </div>
                                <h3 className="text-sm font-semibold text-white mb-1.5">{f.title}</h3>
                                <p className="text-xs text-slate-400 leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="relative z-10 py-20 border-b border-white/5 bg-[rgba(255,255,255,0.012)]">
                <div className="mx-auto max-w-5xl px-4 sm:px-6">
                    <div className="text-center mb-4">
                        <h2 className="text-2xl font-bold text-white">From the people using it</h2>
                        <p className="mt-2 text-sm text-slate-500">Clinical students and practitioners across every stage of training.</p>
                    </div>
                </div>
                <div className="mt-10 overflow-x-auto no-scrollbar">
                    <div className="flex gap-5 px-4 sm:px-6 w-max snap-x snap-mandatory">
                        {TESTIMONIALS.map((t, i) => (
                            <div key={i} className="w-[280px] sm:w-[340px] shrink-0 snap-start rounded-2xl border border-white/8 bg-white/[0.03] p-7 flex flex-col gap-5">
                                {/* Stars */}
                                <div className="flex gap-0.5">
                                    {[...Array(5)].map((_, s) => (
                                        <svg key={s} className="h-4 w-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                    ))}
                                </div>
                                {/* Quote */}
                                <p className="text-sm text-slate-300 leading-relaxed flex-1">&ldquo;{t.quote}&rdquo;</p>
                                {/* Attribution */}
                                <div className="flex items-center gap-3 pt-2 border-t border-white/6">
                                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-teal-500/30 to-emerald-500/30 border border-teal-400/20 flex items-center justify-center shrink-0">
                                        <span className="text-xs font-bold text-teal-400">{t.name.charAt(0)}</span>
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-white">{t.name}</p>
                                        <p className="text-[11px] text-slate-500">{t.role}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div className="shrink-0 w-px sm:w-1" aria-hidden="true" />
                    </div>
                </div>
                <p className="mt-4 text-center text-[11px] text-slate-600 sm:hidden">← swipe to see more →</p>
            </section>

            {/* Pricing */}
            <section id="pricing" ref={pricingSectionRef} className="relative z-10 py-20 px-4 sm:px-6 border-b border-white/5">
                <div className="mx-auto max-w-5xl">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl font-bold text-white">Simple pricing</h2>
                        <p className="text-slate-400 text-sm mt-2">Try free · 1 credit = 1 question or flashcard · Credits refresh monthly</p>
                    </div>

                    <PromoBanner className="max-w-xl mx-auto mb-8" />

                    {/* Free trial */}
                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 mb-8 max-w-xl mx-auto">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <h3 className="text-base font-semibold text-white">Free Trial</h3>
                                <div className="mt-1 flex items-baseline gap-1">
                                    <span className="text-2xl font-bold text-white">$0</span>
                                    <span className="text-slate-400 text-sm">to start</span>
                                </div>
                            </div>
                            <button
                                onClick={handleStart}
                                disabled={!hasFreeLeft}
                                className="shrink-0 rounded-xl px-4 py-2 text-sm font-semibold border border-white/15 text-slate-200 hover:text-white hover:border-white/30 hover:bg-white/5 transition disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {hasFreeLeft ? `Start free (${freeRemaining} left)` : 'Trial complete'}
                            </button>
                        </div>
                        <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-slate-400">
                            <li className="flex items-start gap-1.5"><span className="text-teal-400 font-bold">✓</span> {FREE_TRIAL_TOTAL} free question generations</li>
                            <li className="flex items-start gap-1.5"><span className="text-teal-400 font-bold">✓</span> Step 1, Step 2, Shelf &amp; more</li>
                            <li className="flex items-start gap-1.5"><span className="text-slate-600">✗</span> <span className="text-slate-600">Monthly credit allowance</span></li>
                            <li className="flex items-start gap-1.5"><span className="text-slate-600">✗</span> <span className="text-slate-600">Batch generation</span></li>
                        </ul>
                    </div>

                    {/* Paid tiers — rendered from the same CLINICALS_PLANS_MONTHLY data as the
                        checkout modal and the /clinicals landing page, so price/credits/features
                        can't drift between the three surfaces. */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
                        {CLINICALS_PLANS_MONTHLY.map((plan) => (
                            <div key={plan.id} className={plan.popular ? 'relative' : ''}>
                                {plan.popular && (
                                    <div className="absolute -top-4 inset-x-0 flex justify-center">
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-1.5 text-[11px] font-bold text-white shadow-lg shadow-emerald-500/30 uppercase tracking-wide">
                                            ⭐ Most Popular
                                        </span>
                                    </div>
                                )}
                                <div
                                    className={
                                        plan.popular
                                            ? 'rounded-2xl border-2 border-teal-500/50 bg-gradient-to-b from-teal-500/8 to-emerald-500/5 p-6 pt-8 shadow-lg shadow-teal-500/10'
                                            : 'rounded-2xl border border-white/10 bg-white/[0.03] p-6'
                                    }
                                >
                                    <p
                                        className={`text-[11px] font-semibold uppercase tracking-widest mb-1 ${plan.popular ? '' : 'text-slate-500'}`}
                                        style={plan.popular ? { color: plan.color } : undefined}
                                    >
                                        {plan.name}
                                    </p>
                                    <div className="flex items-baseline gap-1 mb-1">
                                        <span className="text-3xl font-bold text-white">{plan.price}</span>
                                        <span className={plan.popular ? 'text-slate-400 text-sm' : 'text-slate-500 text-sm'}>{plan.period}</span>
                                    </div>
                                    <p className={plan.popular ? 'text-xs text-slate-400 mb-5' : 'text-xs text-slate-500 mb-5'}>{TIER_TAGLINES[plan.id]}</p>
                                    <ul className={`space-y-2 text-xs mb-6 ${plan.popular ? 'text-slate-200' : 'text-slate-300'}`}>
                                        {plan.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-start gap-2">
                                                <span className="font-bold shrink-0" style={{ color: plan.color }}>✓</span> {feature}
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => onOpenPlanChooser?.({ isClinicals: true })}
                                        className={
                                            plan.popular
                                                ? 'w-full rounded-xl py-2.5 text-sm font-semibold bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white transition shadow-lg shadow-teal-500/20'
                                                : 'w-full rounded-xl py-2.5 text-sm font-semibold border transition hover:bg-white/5'
                                        }
                                        style={!plan.popular ? { borderColor: `${plan.color}66`, color: plan.color } : undefined}
                                    >
                                        Get {plan.name}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <p className="text-center text-xs text-slate-600 mt-8">Cancel anytime. No contracts. Extra credit packs available for subscribers.</p>
                </div>
            </section>

            {/* Competitor comparison */}
            <section className="relative z-10 py-20 px-4 sm:px-6 border-b border-white/5 bg-[rgba(255,255,255,0.012)]">
                <div className="mx-auto max-w-5xl">
                    <div className="text-center mb-4">
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">How the price compares</h2>
                        <p className="mt-3 text-slate-400 text-sm max-w-xl mx-auto">Everyone else sells you the same static bank every other subscriber gets. We build yours from your own material.</p>
                    </div>
                </div>
                <div className="mt-10 overflow-x-auto no-scrollbar">
                    <div className="mx-auto w-max min-w-full sm:min-w-0 px-4 sm:px-6">
                        <table className="w-full max-w-4xl mx-auto text-sm border-separate" style={{ borderSpacing: 0 }}>
                            <thead>
                                <tr>
                                    <th className="text-left text-xs font-semibold uppercase tracking-widest text-slate-500 pb-3 pr-4 w-40">&nbsp;</th>
                                    <th className="text-center pb-3 px-4 min-w-[160px]">
                                        <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-400/25 bg-teal-500/10 px-3 py-1 text-xs font-semibold text-teal-400">FlashFonic</span>
                                    </th>
                                    <th className="text-center pb-3 px-4 min-w-[140px] text-xs font-semibold text-slate-400">StatPearls</th>
                                    <th className="text-center pb-3 px-4 min-w-[140px] text-xs font-semibold text-slate-400">AMBOSS</th>
                                    <th className="text-center pb-3 px-4 min-w-[140px] text-xs font-semibold text-slate-400">UWorld</th>
                                </tr>
                            </thead>
                            <tbody className="align-top">
                                <tr>
                                    <td className="text-xs text-slate-500 py-4 pr-4 border-t border-white/8">Price</td>
                                    <td className="text-center py-4 px-4 border-t border-teal-400/20 bg-teal-500/5 rounded-t-xl">
                                        <span className="text-lg font-bold text-white">$34.99</span><span className="text-slate-500 text-xs">/mo</span>
                                    </td>
                                    <td className="text-center py-4 px-4 border-t border-white/8 text-slate-300">$249<span className="text-slate-500 text-xs">/mo</span></td>
                                    <td className="text-center py-4 px-4 border-t border-white/8 text-slate-300">$29.99<span className="text-slate-500 text-xs">/mo</span></td>
                                    <td className="text-center py-4 px-4 border-t border-white/8 text-slate-300">$349<span className="text-slate-500 text-xs">/30 days</span></td>
                                </tr>
                                <tr>
                                    <td className="text-xs text-slate-500 py-4 pr-4 border-t border-white/6">Content source</td>
                                    <td className="text-center py-4 px-4 border-t border-teal-400/20 bg-teal-500/5 text-sm text-emerald-300 font-medium">Your own notes &amp; lectures</td>
                                    <td className="text-center py-4 px-4 border-t border-white/6 text-slate-500">Fixed, shared bank</td>
                                    <td className="text-center py-4 px-4 border-t border-white/6 text-slate-500">Fixed, shared bank</td>
                                    <td className="text-center py-4 px-4 border-t border-white/6 text-slate-500">Fixed, shared bank</td>
                                </tr>
                                <tr>
                                    <td className="text-xs text-slate-500 py-4 pr-4 border-t border-white/6">Monthly volume</td>
                                    <td className="text-center py-4 px-4 border-t border-teal-400/20 bg-teal-500/5 text-sm text-slate-200">4,000 credits — cards + board questions</td>
                                    <td className="text-center py-4 px-4 border-t border-white/6 text-slate-500">Unlimited (same bank as everyone)</td>
                                    <td className="text-center py-4 px-4 border-t border-white/6 text-slate-500">50 Q-bank questions</td>
                                    <td className="text-center py-4 px-4 border-t border-white/6 text-slate-500">Full bank, 30-day window</td>
                                </tr>
                                <tr>
                                    <td className="text-xs text-slate-500 py-4 pr-4 border-t border-white/6 rounded-bl-xl">Personalized to you</td>
                                    <td className="text-center py-4 px-4 border-t border-teal-400/20 bg-teal-500/5 rounded-b-xl">
                                        <svg className="inline h-4 w-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                                    </td>
                                    <td className="text-center py-4 px-4 border-t border-white/6 text-slate-600">—</td>
                                    <td className="text-center py-4 px-4 border-t border-white/6 text-slate-600">—</td>
                                    <td className="text-center py-4 px-4 border-t border-white/6 text-slate-600 rounded-br-xl">—</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <p className="mt-6 text-center text-[11px] text-slate-600 max-w-2xl mx-auto px-4">Competitor pricing shown is publicly listed pricing as of this writing and may change — check each provider directly for current rates. AMBOSS clinician plan shown; UWorld shown at its 30-day rate.</p>
            </section>

            {/* Footer */}
            <footer className="relative z-10 py-12 px-4 sm:px-6">
                <div className="mx-auto max-w-5xl">
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-8 mb-8">
                        <div>
                            <a href="/" className="text-lg font-bold tracking-tight text-white">
                                flash<span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">fonic</span>
                            </a>
                            <p className="mt-2 text-xs text-slate-600 max-w-xs leading-relaxed">Clinical study tools for every stage of healthcare training. Built by a physician.</p>
                        </div>
                        <div className="flex flex-wrap gap-x-10 gap-y-6">
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">Pathways</p>
                                <div className="flex flex-col gap-2">
                                    {PATHWAY_LIST.slice(0, 5).map(p => (
                                        <a key={p.slug} href={`/for/${p.slug}`} className="text-xs text-slate-500 hover:text-slate-300 transition">{p.title}</a>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">&nbsp;</p>
                                <div className="flex flex-col gap-2">
                                    {PATHWAY_LIST.slice(5).map(p => (
                                        <a key={p.slug} href={`/for/${p.slug}`} className="text-xs text-slate-500 hover:text-slate-300 transition">{p.title}</a>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">Legal</p>
                                <div className="flex flex-col gap-2">
                                    <a href="/terms" className="text-xs text-slate-500 hover:text-slate-300 transition">Terms &amp; Conditions</a>
                                    <a href="/privacy" className="text-xs text-slate-500 hover:text-slate-300 transition">Privacy Policy</a>
                                    <a href="mailto:support@flashfonic.com" className="text-xs text-slate-500 hover:text-slate-300 transition">Contact Us</a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="border-t border-white/5 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2">
                        <p className="text-xs text-slate-600">&copy; FlashFonic, Trifecta Pro LLC</p>
                        <p className="text-xs text-slate-700">Generated questions are not a substitute for clinical judgment or official study resources.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
